import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FormTitle from 'components/PageLayout/FormTitle';
import ApplyForm, {
  ApplyFormFields,
  IApplyFormValues,
} from 'components/curo-components/forms/ApplyForm';
import { GoogleAnalyticsEventType } from 'enums/GoogleAnalyticsEventType';
import { CuroAppRoute } from 'enums/curo/CuroAppRoute';
import { EnvironmentVariable } from 'enums/curo/EnvironmentVariable';
import { PrescreenedOfferType } from 'enums/curo/PrescreenedOfferType';
import { envs } from 'envs';
import { setApplyProcessData } from 'handlers/curo-handlers/applyProcessSlice';
import { getApplyProcessState } from 'handlers/curo-handlers/selectors';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useGoogleAnalytics from 'hooks/useGoogleAnalytics';
import { ApplicationIsNotApprovedNOAA, ExistingLoanPage } from 'pages/CuroPages/EndPages';
import { IDefaultStepProps } from 'pages/CuroPages/types';
import { borrowerStateCheck, checkExistingApplication } from 'thunks';
import styled from 'types/AppTheme';
import { formatToMonetary } from 'utils/curo-helpers/formatters';

import ExistingApplicationModal from './ExistingApplicationModal';
import ExistingApplicationWithoutOffer from './ExistingApplicationWithoutOffer';
import QualifyModal from './QualifyModal';

enum ApplyFormSteps {
  ApplyFormStep = 'ApplyFormStep',
  ApplicationDeclinedStep = 'ADStep',
  ApplicationFundedStep = 'AFStep',
  ApplicationInProgressStep = 'AIPStep',
}

const StyledLink = styled.a`
  color: inherit;
`;

interface IApplyFormStepProps extends IDefaultStepProps {}

type ApplyFormErrors = Record<ApplyFormFields.BorrowerState, string>;

const getFormDMTitle = (borrowerFirstName: string) => <>Congrats, {borrowerFirstName}!</>;
const FORM_NO_MATCH_TITLE = 'See if you’re pre-qualified for loan offers!';

const getFormDMSubitle = (offerAmount: string) => (
  <>
    We found your pre-approved loan of {offerAmount}. Just fill out the application forms, the money
    will be in your account by the next business day *!
  </>
);
const FORM_NO_MATCH_SUBTITLE =
  'No commitment – Pre-qualification does not impact your credit score. *';

const FORM_DM_FOOTER =
  'You must continue to meet eligibility requirements at the time of application. Personal property collateral may be requested. Applicants enrolled with a debt settlement agency or consumer credit counseling service are ineligible for this offer. Subject to state regulations, eligibility, credit check, underwriting and approval. Rates, terms and conditions apply.';
const FORM_NO_MATCH_FOOTER =
  'To see if you pre-qualify, Heights Finance will conduct a “soft” credit inquiry to determine eligibility. This inquiry does not impact your credit score. If you decide to move forward with applying for a loan, Heights Finance will order a credit report using a “hard” credit inquiry. A hard inquiry may impact your credit score.';

const UNAVAILABLE_STATE_ERROR_MESSAGE = (
  <>
    We currently don't offer online loans in your state.{' '}
    <StyledLink
      target="_blank"
      href={envs[EnvironmentVariable.FindBranchLink]}
      rel="noopener noreferrer"
    >
      Click here
    </StyledLink>{' '}
    to find a branch near you
  </>
);

const ApplyFormStep = ({ onStepComplete }: IApplyFormStepProps) => {
  const trackEvent = useGoogleAnalytics();
  const [modalType, setModalType] = useState<
    'existingApplication' | 'qualify' | 'existingApplicationWithoutOffer' | null
  >(null);
  const [applyFormProcessStep, setApplyFormProcessStep] = useState<ApplyFormSteps>(
    ApplyFormSteps.ApplyFormStep,
  );
  const [applyFormErrors, setApplyFormErrors] = useState<ApplyFormErrors>({
    [ApplyFormFields.BorrowerState]: '',
  });

  const { applyProcessData, mailOfferData } = useAppSelector(getApplyProcessState);
  const dispatch = useAppDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();

  const isPrescreenedOfferTypeOnline = mailOfferData?.actionsType === PrescreenedOfferType.Online;
  const borrowerFirstName = mailOfferData?.firstName;
  const borrowerOfferAmount = mailOfferData?.offerAmount;

  useEffect(() => {
    if (isPrescreenedOfferTypeOnline) {
      trackEvent(GoogleAnalyticsEventType.DmPersonalInfo);
    } else {
      trackEvent(GoogleAnalyticsEventType.ApplyProcessPersonalInfo);
    }
  }, [isPrescreenedOfferTypeOnline]);

  const getApplyFormTitle = () => {
    if (isPrescreenedOfferTypeOnline && borrowerFirstName) {
      return getFormDMTitle(borrowerFirstName);
    }
    return FORM_NO_MATCH_TITLE;
  };

  const getApplyFormSubtitle = () => {
    if (isPrescreenedOfferTypeOnline && borrowerOfferAmount) {
      return getFormDMSubitle(formatToMonetary(Number(borrowerOfferAmount)));
    }
    return FORM_NO_MATCH_SUBTITLE;
  };

  const handleSubmit = async (values: IApplyFormValues) => {
    await dispatch(setApplyProcessData(values));

    const { nextStep: existingApplicationStep, isNew } = await dispatchWithUnwrap(
      checkExistingApplication({ borrowerPersonalIdNumber: values[ApplyFormFields.BorrowerSSN] }),
    );

    if (!existingApplicationStep && !isNew) {
      // CURO-TODO run strategy and show modals
      const { passed: stateCheckPassed } = await dispatchWithUnwrap(
        borrowerStateCheck(values[ApplyFormFields.BorrowerState]),
      );

      if (!stateCheckPassed) {
        setApplyFormErrors({
          // CURO-TODO: Cannot find better way to cast this, otherwise we need to change 10+ type in product_modules
          [ApplyFormFields.BorrowerState]: UNAVAILABLE_STATE_ERROR_MESSAGE as unknown as string,
        });

        return;
      }
      
      onStepComplete();
      return;
    }

    if (!existingApplicationStep && isNew) {
      setModalType('existingApplicationWithoutOffer');
      return;
    }

    switch (existingApplicationStep) {
      case ApplyFormSteps.ApplicationDeclinedStep:
        return setApplyFormProcessStep(ApplyFormSteps.ApplicationDeclinedStep);
      case ApplyFormSteps.ApplicationFundedStep:
        return setApplyFormProcessStep(ApplyFormSteps.ApplicationFundedStep);
      case ApplyFormSteps.ApplicationInProgressStep:
        setModalType('existingApplication');
        setApplyFormProcessStep(ApplyFormSteps.ApplicationInProgressStep);
        return;
      default:
        return null as never;
    }
  };

  const closeModal = () => setModalType(null);

  const renderModals = () => {
    switch (modalType) {
      case 'existingApplication':
        return (
          <ExistingApplicationModal
            onButtonClick={() => {
              navigate(CuroAppRoute.SignIn);
            }}
            onClose={closeModal}
          />
        );
      case 'existingApplicationWithoutOffer':
        return (
          <ExistingApplicationWithoutOffer
            onButtonClick={() => {
              navigate(CuroAppRoute.SignIn);
            }}
            onClose={closeModal}
          />
        );
      case 'qualify':
        return <QualifyModal onClose={closeModal} onConfirm={onStepComplete} />;
      default:
        return null;
    }
  };

  const renderStep = () => {
    switch (applyFormProcessStep) {
      case ApplyFormSteps.ApplyFormStep:
      case ApplyFormSteps.ApplicationInProgressStep:
        return (
          <>
            <FormTitle title={getApplyFormTitle()} subTitle={getApplyFormSubtitle()} />

            <ApplyForm
              isPrescreenedOfferTypeOnline={isPrescreenedOfferTypeOnline}
              handleSubmit={handleSubmit}
              initialData={applyProcessData}
              externalErrors={applyFormErrors}
              resetExternalErrors={(fieldName) =>
                setApplyFormErrors((prev) => ({ ...prev, [fieldName]: '' }))
              }
              footerText={isPrescreenedOfferTypeOnline ? FORM_DM_FOOTER : FORM_NO_MATCH_FOOTER}
            />
            {renderModals()}
          </>
        );
      case ApplyFormSteps.ApplicationDeclinedStep:
        return <ApplicationIsNotApprovedNOAA />;
      case ApplyFormSteps.ApplicationFundedStep:
        return <ExistingLoanPage />;
      default:
        return null as never;
    }
  };

  return renderStep();
};

export default ApplyFormStep;
